import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button2 } from "@/ui2/atoms/Button";
import { DashboardCard2 } from "@/ui2/molecules/DashboardCard2";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getBadges } from "@/components/Provider/PatientView/MemberProfile/Notes/helpers";
import { selectProfile } from "@/redux/selectors/authSelectors";

import { AccessingPHIDisclaimer } from "../../../../components/Common/AccessingPHIDisclaimer";
import { api } from "../../../../APIRequests";
import { calculateAge } from "../../../../utils/momentUtils";
import DeleteNote from "../../Providers/PatientProfileView/DeleteNote";
import { formatMobileNumber } from "../../../../utils/filters";
import GenericNoteView from "../../../../components/Common/Note/GenericNoteView";
import images from "../../../../utils/images";
import { MemberHeader } from "../../../../component-library/MemberHeader";
import NoteActions from "../../../../components/Common/Note/NoteActions";
import { usePatient, usePatientDiagnosis } from "../../../../utils/hooks/usePatient";

import { NoteAddendum } from "./NoteAddendum";

export function ViewFullNote() {
  const { noteId, patientId } = useParams();
  const profile = useSelector(selectProfile);
  const history = useHistory();
  const patient = usePatient(patientId, history.location.state?.patientDetails);
  const diagnosis = usePatientDiagnosis(patientId, history.location.state?.patientDiagnosis);
  const primaryDiagnosis = _.get(diagnosis, "ICD_10_axis_1_diagnosis", [])
    .map((icd_10) => icd_10["value"])
    .join(", ");

  const secondaryDiagnosis = _.get(diagnosis, "ICD_10_axis_2_diagnosis", [])
    .map((icd_10) => icd_10["value"])
    .join(", ");

  const [note, setNote] = useState(null);
  const [showDeleteNotePopUp, setShowDeleteNotePopUp] = useState(false);
  const [draftNoteId, setDraftNoteId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [addendums, setAddendums] = useState([]);

  const getNote = async () => {
    try {
      let urlParams = { patientId };
      const queryParams = { note_id: noteId };
      let note = await api.shared.fetch_patient_progress_notes({ queryParams, urlParams });

      setNote(note);

      const data = {
        note_id: noteId,
        patient_id: patientId,
      };
      const addendumsResponse = await api.shared.fetch_note_addendums({ data, urlParams });

      setAddendums(addendumsResponse.addendums);
    } catch (e) {
      console.log(e);
    }
  };

  const isNoteEditable = (note) => {
    if (!note) return false;

    return note.note_state === "draft" && profile?.username === note.created_by;
  };

  const deleteNote = async (patientId, noteId) => {
    let data = { id: noteId };
    let urlParams = { patientId: patientId };

    await api.provider.delete_progress_note({ data, urlParams });
    setShowDeleteNotePopUp(false);
    goBack();
  };

  const goBack = () => {
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  const shouldDisplayAddendumForm =
    note?.created_by === profile?.username &&
    note?.signature &&
    note?.note_state !== "draft" &&
    note?.note_type !== "Advanced Care Activity" &&
    addendums.length < 5;

  useEffect(() => {
    getNote();
  }, []);

  const isEditable = isNoteEditable(note);

  return (
    <>
      <Button2
        className="tw:flex tw:max-w-min tw:gap-2 tw:print:hidden"
        classNameChildren="tw:flex tw:gap-2 tw:text-text-tertiary tw:font-medium"
        icon={<img alt="back" className="tw:max-w-full" src={images("./common/solidBckBtn.svg")} />}
        onClick={goBack}
        variant="blank"
      >
        Back to Patient Profile
      </Button2>

      {showDeleteNotePopUp && (
        <DeleteNote
          clickHandler={() => deleteNote(patientId, draftNoteId)}
          close={() => {
            setShowDeleteNotePopUp(false);
          }}
        />
      )}

      <DashboardCard2 className="tw:flex tw:p-5!" classNameChildren="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-row tw:justify-between tw:gap-6">
          <AccessingPHIDisclaimer />

          <NoteActions
            isEditable={isEditable}
            isEditing={isEditing}
            note={note}
            patientId={patientId}
            setDraftNoteId={setDraftNoteId}
            setIsEditing={setIsEditing}
            setShowDeleteNotePopUp={setShowDeleteNotePopUp}
          />
        </div>

        {note && (
          <>
            <Divider2 />
            <ul className="tw:flex tw:items-center tw:gap-1">{getBadges(note)}</ul>
          </>
        )}
      </DashboardCard2>

      {patient && diagnosis && (
        <MemberHeader
          age={calculateAge(_.get(patient, "dob", ""))}
          dayOfCare={_.get(patient, "number_of_days_in_care", "")}
          dob={_.get(patient, "dob")}
          memberName={`${_.get(patient, "first_name", "")} ${_.get(patient, "last_name", "")}`}
          phoneNumber={formatMobileNumber(_.get(patient, "mobile"))}
          primaryDiagnosis={primaryDiagnosis === "" ? "None" : primaryDiagnosis}
          secondaryDiagnosis={secondaryDiagnosis === "" ? "None" : secondaryDiagnosis}
        />
      )}

      <GenericNoteView
        isEditing={isEditing}
        note={note}
        patient={patient}
        patientName={patient && `${patient.first_name} ${patient.last_name}`}
        refetchNote={getNote}
        setIsEditing={setIsEditing}
      />

      {note?.note_state !== "draft" &&
        addendums.map((addendum, index) => (
          <NoteAddendum
            addendum={addendum.addendum}
            credentials={addendum.credentials}
            key={addendum.id || index}
            noteId={note?.id}
            patientId={patientId}
            signature={addendum.signature}
            textTestId={`note-addendum-text-${index}`}
            timestamp={addendum.timestamp}
          />
        ))}

      {shouldDisplayAddendumForm && (
        <NoteAddendum
          credentials={note?.provider_cp_credentials}
          noteId={note?.id}
          onSubmit={(addendum) => setAddendums([...addendums, addendum])}
          patientId={patientId}
          signature={note?.signature}
        />
      )}
    </>
  );
}
